import { Check, ChevronsUpDown, InfoIcon } from 'lucide-react';
import { useState } from 'react';

import { NewsFrequencyChart } from '@/components/charts/NewsFrequencyChart';
import { PriceChart } from '@/components/charts/PriceChart';
import { Footer } from '@/components/Footer';
import { Navbar } from '@/components/Navbar';
import { TimeWindowSelector } from '@/components/TimeWindowSelector';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useSymbols } from '@/hooks/useSymbols';
import { cn } from '@/lib/utils';
import { TimeWindow } from '@/types';
import {
  getTimeWindowDescription,
  ROMANIAN_TIME_WINDOW_LABELS,
} from '@/utils/timeWindow';

// Main component
export default function GraphsPage() {
  const [selectedSymbol, setSelectedSymbol] = useState<string>('');
  const [selectedTimeWindow, setSelectedTimeWindow] =
    useState<TimeWindow>('1week');

  const { data: symbolsData, isLoading: symbolsLoading } = useSymbols();

  const handleTimeWindowChange = (window: TimeWindow) => {
    setSelectedTimeWindow(window);
  };

  // Symbol selector component for reuse
  const SymbolSelector = ({
    value,
    onChange,
    disabled,
    label,
  }: {
    value: string;
    onChange: (symbol: string) => void;
    disabled: boolean;
    label: string;
  }) => {
    const [open, setOpen] = useState(false);

    return (
      <div className="mb-4 w-full max-w-xs">
        <label className="mb-2 block text-sm font-medium text-neutral-700">
          {label}
        </label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full justify-between border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-100"
              disabled={disabled}>
              {value ? value : 'Selectează simbolul'}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0">
            <Command>
              <CommandInput placeholder="Caută simbol..." className="h-9" />
              <CommandEmpty>No symbol found.</CommandEmpty>
              <CommandGroup className="max-h-64 overflow-auto">
                {symbolsLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-neutral-800 border-t-transparent"></div>
                  </div>
                ) : (
                  symbolsData?.symbols?.map((symbol: string) => (
                    <CommandItem
                      key={symbol}
                      value={symbol}
                      onSelect={() => {
                        onChange(symbol);
                        setOpen(false);
                      }}
                      className="flex items-center">
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          value === symbol ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <span>{symbol}</span>
                    </CommandItem>
                  ))
                )}
              </CommandGroup>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    );
  };

  return (
    <div className="flex min-h-screen flex-col bg-neutral-50 text-neutral-800">
      <Navbar />

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 pb-8 pt-28 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="mb-6 font-heading text-2xl font-bold">
            Grafice de piață
          </h1>

          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <SymbolSelector
              value={selectedSymbol}
              onChange={setSelectedSymbol}
              disabled={symbolsLoading}
              label="Selectează simbolul"
            />
            <div className="mb-4 w-full max-w-xs">
              <label className="mb-2 block text-sm font-medium text-neutral-700">
                Selectează fereastra de timp
              </label>
              <TimeWindowSelector
                selectedTimeWindow={selectedTimeWindow}
                onTimeWindowChange={handleTimeWindowChange}
                labels={ROMANIAN_TIME_WINDOW_LABELS}
              />
            </div>
          </div>

          {/* Price Charts Section */}
          <div className="mb-8">
            {selectedSymbol ? (
              <PriceChart
                symbol={selectedSymbol}
                timeWindow={selectedTimeWindow}
                onTimeWindowChange={handleTimeWindowChange}
                title={`Graficul prețului pentru ${selectedSymbol}`}
                description={getTimeWindowDescription(
                  selectedTimeWindow,
                  'price'
                )}
                timeWindowLabels={ROMANIAN_TIME_WINDOW_LABELS}
                emptyStateMessage={`Nu există date despre preț pentru ${selectedSymbol}`}
                showTimeWindowControls={false}
              />
            ) : (
              <Card className="flex flex-col items-center justify-center p-8 text-center shadow-md">
                <h3 className="mb-2 text-xl font-semibold">
                  Selectează un simbol
                </h3>
                <p className="mb-4 text-neutral-600">
                  Alege un simbol pentru a vedea graficele de preț
                </p>
                <InfoIcon className="h-12 w-12 text-neutral-400" />
              </Card>
            )}
          </div>

          {/* News Frequency Section */}
          <div>
            {selectedSymbol ? (
              <NewsFrequencyChart
                symbol={selectedSymbol}
                timeWindow={selectedTimeWindow}
                onTimeWindowChange={handleTimeWindowChange}
                title={`Frecvența știrilor pentru ${selectedSymbol}`}
                description={getTimeWindowDescription(
                  selectedTimeWindow,
                  'news'
                )}
                timeWindowLabels={ROMANIAN_TIME_WINDOW_LABELS}
                emptyStateMessage={`Nu s-au găsit articole de știri pentru ${selectedSymbol}.`}
                showTimeWindowControls={false}
              />
            ) : (
              <Card className="flex flex-col items-center justify-center p-8 text-center shadow-md">
                <h3 className="mb-2 text-xl font-semibold">
                  Selectează un simbol
                </h3>
                <p className="mb-4 text-neutral-600">
                  Alege un simbol pentru a vedea frecvența știrilor
                </p>
                <InfoIcon className="h-12 w-12 text-neutral-400" />
              </Card>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
